import { useEffect, useState, useContext, useMemo } from 'react';
import { AuthContext } from '../../contexts/auth';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../../Utils/locale';
import { useCurrency } from '../../contexts/Currency';
import { AppLayout } from '../../components/AppLayout';
import { CardIcon } from '../../components/CardIcon';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Banknote, Calendar, TrendingUp, DollarSign, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { getIncomeHistory, saveIncomeEntry } from '../../api/incomeHistory';
import { CurrencyInput } from '../../components/ui/currency-input';

function IncomeHistory() {
  const { user } = useContext(AuthContext);
  const { t, i18n } = useTranslation();

  const [entries, setEntries] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editYear, setEditYear] = useState(String(new Date().getFullYear()));
  const [editMonth, setEditMonth] = useState(String(new Date().getMonth() + 1));
  const [editValue, setEditValue] = useState('');

  const defaultIncome = Number(user?.receita) || 0;

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const data = await getIncomeHistory(12);
      setEntries(data || []);
    } catch {
      console.error('Erro ao carregar historico de renda');
    }
  };

  const months12 = useMemo(() => {
    const now = new Date();
    const result = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      result.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }
    return result;
  }, []);

  const entriesMap = useMemo(() => {
    const map = {};
    (entries || []).forEach(e => {
      map[`${e.year}-${e.month}`] = e.income;
    });
    return map;
  }, [entries]);

  const tableData = useMemo(() => {
    return months12.map(({ year, month }) => {
      const key = `${year}-${month}`;
      const customValue = entriesMap[key];
      const isDefault = customValue === undefined;
      const income = isDefault ? defaultIncome : customValue;
      const label = new Intl.DateTimeFormat(intlLocale(i18n.language), {
        month: 'long',
        year: 'numeric'
      }).format(new Date(year, month - 1, 1));
      return { year, month, income, isDefault, label };
    });
  }, [months12, entriesMap, defaultIncome, i18n.language]);

  const currentMonthIncome = tableData[0]?.income || 0;
  const average = tableData.reduce((sum, r) => sum + r.income, 0) / tableData.length;
  const annualTotal = tableData.reduce((sum, r) => sum + r.income, 0);

  const { format: formatCurrency } = useCurrency();

  const openEditDialog = (year, month, income) => {
    setEditYear(String(year));
    setEditMonth(String(month));
    setEditValue(String(income || ''));
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    const val = Number(editValue);
    if (!Number.isFinite(val) || val < 0) {
      toast.error(t('enterValidAmount'));
      return;
    }

    try {
      await saveIncomeEntry(parseInt(editYear), parseInt(editMonth), val);
      toast.success(t('incomeSavedSuccess'));
      setIsDialogOpen(false);
      await loadEntries();
    } catch {
      toast.error(t('errorSavingIncome'));
    }
  };

  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const label = new Intl.DateTimeFormat(intlLocale(i18n.language), { month: 'long' })
        .format(new Date(2026, i, 1));
      return { value: String(i + 1), label: label.charAt(0).toUpperCase() + label.slice(1) };
    });
  }, [i18n.language]);

  const currentYear = new Date().getFullYear();

  return (
    <AppLayout
      title={t('manageMonthlyIncome')}
      subtitle={t('incomeHistorySubtitle')}
      icon={Banknote}
    >
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <CardIcon icon={DollarSign} className="bg-gradient-to-br from-blue-500 to-indigo-600" iconClassName="text-white" />
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t('currentMonth')}</p>
                  <p className="text-xl font-semibold text-slate-900">{formatCurrency(currentMonthIncome)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <CardIcon icon={TrendingUp} className="bg-gradient-to-br from-green-500 to-emerald-600" iconClassName="text-white" />
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t('average12Months')}</p>
                  <p className="text-xl font-semibold text-slate-900">{formatCurrency(average)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <CardIcon icon={Calendar} className="bg-gradient-to-br from-purple-500 to-pink-600" iconClassName="text-white" />
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t('annualTotal')}</p>
                  <p className="text-xl font-semibold text-slate-900">{formatCurrency(annualTotal)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <div className="flex items-center justify-between border-b p-4">
            <h3 className="text-base font-semibold text-slate-900">{t('monthlyHistory')}</h3>
            <Button size="sm" onClick={() => openEditDialog(currentYear, new Date().getMonth() + 1, currentMonthIncome)}>
              <Edit className="w-4 h-4 mr-1" />
              {t('editMonthlyIncome')}
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>{t('monthYear')}</TableHead>
                <TableHead>{t('incomeValue')}</TableHead>
                <TableHead className="text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableData.map((row) => (
                <TableRow key={`${row.year}-${row.month}`} className="hover:bg-slate-50">
                  <TableCell className="font-medium capitalize">{row.label}</TableCell>
                  <TableCell>
                    {formatCurrency(row.income)}
                    {row.isDefault && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        {t('defaultLabel')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(row.year, row.month, row.income)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('editMonthlyIncome')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('year')}</Label>
                <Select value={editYear} onValueChange={setEditYear}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('month')}</Label>
                <Select value={editMonth} onValueChange={setEditMonth}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('incomeValue')}</Label>
              <CurrencyInput
                value={editValue}
                onValueChange={(val) => setEditValue(val)}
                language={i18n.language}
                placeholder={t('currencyPlaceholder')}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button onClick={handleSave}>
                {t('save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default IncomeHistory;
